import { match, P } from 'ts-pattern'

import { Checkbox, RadioGroup, Select, TextInput } from '@codecorp/components'
import { CountryInput } from './country'
import { PhoneInput } from './phone'
import { camelToTitle } from '../utils/camel-to-title'
import { getFieldDescription } from '../utils/field-description'
import { CustomFieldProps } from './types'
import { parseZodType } from 'zod-parser'

export const FormField = ({
    id,
    name,
    type,
    issues,
    defaultValue,
    onChange,
    disabled,
}: CustomFieldProps) => {
    // as a convention add @inputType= to the description to indicate that this field should be rendered as a specific input
    const {
        autoComplete,
        inputType,
        labels = [],
        label,
        tooltip,
        placeHolder,
        className,
    } = getFieldDescription(type)
    const customValidity = issues.map((x) => x.message).join('\n')

    return (
        <>
            <label htmlFor={id}>
                {label ?? camelToTitle(name)}
                {tooltip && (
                    <span className="ml-2 text-xs text-gray-500">
                        ({tooltip})
                    </span>
                )}
            </label>

            <div
                className="flex flex-col"
                key={id}
            >
                {match({
                    // so stack traces include the component name. shouldn't match on this
                    __DEBUG_DISPLAYNAME: name,

                    ...parseZodType(type),
                    inputType,
                })
                    // explicit types, preferred
                    .with({ inputType: 'text' }, () => (
                        <TextInput
                            id={id}
                            name={name}
                            autoComplete={autoComplete}
                            customValidity={customValidity}
                            defaultValue={defaultValue}
                        />
                    ))
                    .with({ inputType: 'country' }, () => (
                        <CountryInput
                            name={name}
                            defaultValue={defaultValue}
                            customValidity={customValidity}
                        />
                    ))
                    .with({ inputType: 'tel' }, () => (
                        <PhoneInput
                            name={name}
                            id={id}
                            defaultValue={defaultValue}
                            customValidity={customValidity}
                        />
                    ))

                    .with(
                        // this is the type of a boolean as represented in a form
                        { $type: 'ZodEnum', $optional: true, $value: ['on'] },
                        () => (
                            <Checkbox
                                id={id}
                                name={name}
                                defaultValue={defaultValue}
                            />
                        ),
                    )

                    // inferred types
                    .with(
                        P.intersection(
                            { $type: 'ZodEnum' },
                            P.union(
                                {
                                    $value: { length: P.lte(3) },
                                    inputType: P.nullish,
                                },
                                { inputType: 'radio' },
                            ),
                        ),
                        ({ $value: options }) => (
                            <RadioGroup
                                id={id}
                                name={name}
                                items={options.map((o, idx) => ({
                                    label: labels[idx] ?? o,
                                    value: o,
                                }))}
                                defaultValue={defaultValue}
                                onValueChange={onChange}
                                disabled={disabled}
                            />
                        ),
                    )

                    .with({ $type: 'ZodEnum' }, ({ $value: options }) => (
                        <Select
                            name={name}
                            defaultValue={defaultValue}
                            items={options.map((o, idx) => ({
                                label: labels[idx] ?? o,
                                value: o,
                            }))}
                            disabled={disabled}
                            className={className || ''}
                        />
                    ))

                    .with({ $type: 'ZodString' }, () => (
                        <TextInput
                            id={id}
                            name={name}
                            autoComplete={autoComplete}
                            customValidity={customValidity}
                            defaultValue={defaultValue}
                            placeholder={placeHolder}
                        />
                    ))

                    .with({ $type: 'ZodNumber' }, () => (
                        <TextInput
                            id={id}
                            name={name}
                            autoComplete={autoComplete}
                            customValidity={customValidity}
                            defaultValue={defaultValue}
                            type="number"
                        />
                    ))
                    .run()}
                {issues.map((issue) => (
                    <div className="text-pink-600">{issue.message}</div>
                ))}
            </div>
        </>
    )
}
